// src/pages/AddInvoice.jsx - Updated with ViewInvoice functionality and design
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";
import Select from "react-select";
import { pdf } from "@react-pdf/renderer";
import QuotPrintPDF from "../components/invoices/QuotPrintPDF"; // Import the PDF component

// Icon imports
import IconX from "../components/icons/IconX";
import IconDownload from "../components/icons/IconDownload";
import IconSave from "../components/icons/IconSave";
import IconTrash from "../components/icons/IconTrash";
import IconPlus from "../components/icons/IconPlus";
import IconPrinter from "../components/icons/IconPrinter";

// Import services
import {
  createInvoice,
  generateInvoiceId,
  getInvoiceByInvoiceNumber,
} from "../services/invoiceService";
import { getAllItem } from "../services/itemsService";
import { getCustomerById, getCustomers } from "../services/customerService";

// Import utilities
import formatPrice from "../utils/formatPrice";
import parseStringDate from "../utils/parseStringDate";

/**
 * Display notification messages to user using SweetAlert2
 */
const showMessage = (msg = "", type = "success") => {
  const toast = Swal.mixin({
    toast: true,
    position: "top",
    showConfirmButton: false,
    timer: 3000,
    customClass: { container: "toast" },
  });
  toast.fire({
    icon: type,
    title: msg,
    padding: "10px 20px",
  });
};

/**
 * Generate unique ID for items and payments
 */
const generateUniqueId = () => {
  return uuidv4();
};

/**
 * Alias for showMessage to maintain compatibility
 */
const showNotification = showMessage;

/**
 * AddInvoice Component
 *
 * Form for creating new invoices with customer details, items, and payments
 */
const AddInvoice = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Set document title
  useEffect(() => {
    document.title = import.meta.env.VITE_PUBLIC_ADMIN_NAME;
  }, []);

  // ==========================================
  // STATE MANAGEMENT
  // ==========================================

  const [items, setItems] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [job, setJob] = useState(null);
  const [itemsOptions, setItemsOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Items management state
  const [itemParams, setItemParams] = useState({
    id: 0,
    title: "",
    description: "",
    price: 0,
    quantity: 0,
  });

  // Available payment methods
  const paymentMethods = [
    { value: "cash", label: "Cash" },
    { value: "bank", label: "Bank Account" },
    { value: "paypal", label: "PayPal" },
    { value: "upi", label: "UPI Transfer" },
    { value: "card", label: "Credit/Debit Card" },
    { value: "cheque", label: "Cheque" },
  ];

  // Status options for invoice
  const statusOptions = [
    { value: "Not Paid yet", color: "warning" },
    { value: "Paid", color: "success" },
    { value: "Quote", color: "danger" },
    { value: "Draft", color: "info" },
    { value: "Instalment", color: "primary" },
  ];

  // Default invoice parameters
  const today = new Date();
  const oneMonthLater = new Date(today);
  oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);

  const defaultParams = {
    currency: "GBP - British Pound",
    date: today.toISOString().split("T")[0], // Set to today's date (YYYY-MM-DD format)
    label: "",
    invoice: "",
    dueDate: oneMonthLater.toISOString().split("T")[0], // Set to one month from today
    name: "",
    email: "",
    address: "",
    phone: "",
    postCode: "",
    notes: "",
    paymentMethod: "cash",
    discount: 0,
    amount: 0,
    status: { tooltip: "Not Paid yet", color: "warning" },
    payments: [],
  };

  const [params, setParams] = useState(defaultParams);

  // Default payment parameters
  const defaultPaymentParams = {
    amount: 0,
    date: "",
  };

  const [paymentParams, setPaymentParams] = useState(defaultPaymentParams);

  // ==========================================
  // LIFECYCLE EFFECTS
  // ==========================================

  /**
   * Load initial data when component mounts
   */
  useEffect(() => {
    loadInitialData();
  }, [searchParams]);

  // ==========================================
  // DATA LOADING FUNCTIONS
  // ==========================================

  /**
   * Load all initial data (customers, items, invoice ID)
   */
  const loadInitialData = async () => {
    try {
      setLoading(true);
      const updatedParams = { ...params };

      // Load customer from URL params if present
      if (searchParams.get("customer")) {
        const customerIdParam = searchParams.get("customer");
        const jobId = searchParams.get("job");

        const customer = await getCustomerById(customerIdParam);

        if (customer) {
          updatedParams.name = customer.name;
          updatedParams.email = customer.email;
          updatedParams.address = customer.location;
          updatedParams.postCode = customer.postCode;
          updatedParams.phone = customer.phone;
          setCustomerId(customer.id);

          if (customer.jobs && jobId) {
            const foundJob = customer.jobs.find((d) => d.id === jobId);
            setJob(foundJob);
          }
        }
      }

      // Load items for dropdown
      const allItems = await getAllItem();
      if (allItems && Array.isArray(allItems)) {
        const formattedItems = allItems.map((item) => ({
          value: item.name,
          label: item.name,
          desc: item.desc,
        }));
        setItemsOptions(formattedItems);
      } else {
        console.warn("Items data is not an array:", allItems);
        setItemsOptions([]);
      }

      // Load customers for dropdown
      const allCustomers = await getCustomers();
      if (allCustomers && Array.isArray(allCustomers)) {
        const formattedCustomers = allCustomers.map((customer) => ({
          value: customer.id,
          label: customer.name,
        }));
        setCustomerOptions(formattedCustomers);
      } else {
        console.warn("Customers data is not an array:", allCustomers);
        setCustomerOptions([]);
      }

      // Generate invoice ID
      const generatedInvoiceId = await generateInvoiceId();
      if (generatedInvoiceId) {
        updatedParams.invoice = generatedInvoiceId;
      }

      setParams(updatedParams);
    } catch (error) {
      console.error("Error loading initial data:", error);
      showNotification("Error loading data. Please refresh the page.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // CALCULATION FUNCTIONS
  // ==========================================

  /**
   * Calculate subtotal of all items
   */
  const calculateSubTotal = () => {
    return items.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  /**
   * Calculate total amount paid
   */
  const calculateTotalPaid = () => {
    return params.payments.reduce(
      (total, payment) => total + payment.amount,
      0,
    );
  };

  /**
   * Calculate VAT (20% of subtotal - discount applied after VAT)
   */
  const calculateVAT = () => {
    const subtotal = calculateSubTotal();
    const vat = subtotal * 0.2; // 20% VAT on full subtotal
    return Math.max(0, vat);
  };

  /**
   * Calculate final total: Subtotal + VAT - Discount
   * Discount is applied after VAT calculation
   */
  const calculateTotal = () => {
    const subtotal = calculateSubTotal();
    const vat = calculateVAT();
    const discount = params.discount > 0 ? params.discount : 0;
    const total = subtotal + vat - discount;
    return Math.max(0, total); // Ensure total is never negative
  };

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  /**
   * Handle customer selection from dropdown
   */
  const handleCustomerSelect = async (selectedOption) => {
    if (!selectedOption) {
      // Clear customer data if selection is cleared
      setParams({
        ...params,
        name: "",
        email: "",
        address: "",
        phone: "",
        postCode: "",
      });
      setCustomerId("");
      return;
    }

    try {
      const customer = await getCustomerById(selectedOption.value);
      if (customer) {
        setParams({
          ...params,
          name: customer.name,
          email: customer.email,
          address: customer.location,
          phone: customer.phone,
          postCode: customer.postCode || params.postCode,
        });
        setCustomerId(selectedOption.value);
      }
    } catch (error) {
      console.error("Error loading customer:", error);
      showNotification("Error loading customer data", "error");
    }
  };

  /**
   * Handle date input changes (convert to proper format)
   */
  const handleDateChange = (field, value) => {
    if (value) {
      const date = new Date(value);
      setParams({
        ...params,
        [field]: date.toLocaleDateString("en-GB"),
      });
    }
  };

  /**
   * Add new item to the invoice
   */
  const addItem = () => {
    // Validation
    if (!itemParams.title.trim()) {
      showMessage("Please enter item title", "error");
      return;
    }
    if (itemParams.quantity <= 0) {
      showMessage(
        "Please enter valid quantity (must be greater than 0)",
        "error",
      );
      return;
    }
    if (itemParams.price <= 0) {
      showMessage("Please enter valid price", "error");
      return;
    }

    // Add item with unique ID
    // Price entered is including VAT (20%), so we save the net price (excluding VAT)
    // To get net price: price / 1.20
    const netPrice = Number(itemParams.price) / 1.2;
    const newItem = {
      ...itemParams,
      id: Date.now(), // Use timestamp as unique ID
      quantity: Number(itemParams.quantity),
      price: netPrice,
    };

    setItems((prevItems) => [...prevItems, newItem]);

    // Reset form
    setItemParams({
      id: 0,
      title: "",
      description: "",
      price: 0,
      quantity: 0,
    });

    showMessage("Item added successfully", "success");
  };

  /**
   * Remove item from invoice
   */
  const removeItem = (itemToRemove) => {
    setItems((prevItems) =>
      prevItems.filter((item) => item.id !== itemToRemove.id),
    );
    showMessage("Item removed successfully", "success");
  };

  /**
   * Handle item selection from dropdown
   */
  const handleItemSelect = (selectedOption) => {
    if (selectedOption) {
      setItemParams((prev) => ({
        ...prev,
        title: selectedOption.label,
        description: selectedOption.desc,
      }));
    }
  };

  /**
   * Add payment to the invoice
   */
  const addPayment = () => {
    // Validation
    if (paymentParams.amount === 0 || !paymentParams.date) {
      showNotification(
        "Please fill in the required payment fields [Amount & Date]",
        "error",
      );
      return;
    }

    if (
      calculateTotalPaid() + Number(paymentParams.amount) >
      calculateTotal()
    ) {
      showNotification(
        `You cannot add more payment than ${formatPrice(calculateTotal())}`,
        "error",
      );
      setPaymentParams(defaultPaymentParams);
      return;
    }

    // Add payment
    setParams({
      ...params,
      payments: [
        ...params.payments,
        { ...paymentParams, id: generateUniqueId() },
      ],
    });
    setPaymentParams(defaultPaymentParams);
    showNotification("Payment added successfully", "success");
  };

  /**
   * Remove payment from the invoice
   */
  const removePayment = (paymentId) => {
    setParams({
      ...params,
      payments: params.payments.filter((payment) => payment.id !== paymentId),
    });
    showNotification("Payment removed", "success");
  };

  /**
   * Save the invoice
   */
  const saveInvoice = async () => {
    try {
      setLoading(true);

      // Validation
      const emptyFields = [];
      if (!params.name) emptyFields.push("Name");
      if (!params.phone) emptyFields.push("Phone");
      if (!params.paymentMethod) emptyFields.push("Payment Method");
      if (!params.currency) emptyFields.push("Currency");
      if (!params.date) emptyFields.push("Date");
      if (!params.dueDate) emptyFields.push("Due Date");

      if (emptyFields.length > 0) {
        showNotification(
          `Please fill in the required fields: ${emptyFields.join(", ")}`,
          "error",
        );
        return;
      }

      if (items.length === 0) {
        showNotification("Please add at least one item", "error");
        return;
      }

      // Check if invoice number already exists
      const existingInvoice = await getInvoiceByInvoiceNumber(params.invoice);
      if (existingInvoice) {
        showNotification(
          `Invoice number #${params.invoice} already exists`,
          "error",
        );
        return;
      }

      // Prepare invoice data
      const total = calculateTotal();
      const invoiceData = {
        ...params,
        payments: params.payments.map((payment) => ({
          amount: payment.amount,
          date: payment.date,
        })),
        items: [...items],
        amount: Number(total),
        customerId: customerId || undefined,
        jobId: job?.id || undefined,
      };

      // DEBUG: Log before removing undefined

      // Remove undefined properties
      Object.keys(invoiceData).forEach((key) => {
        if (invoiceData[key] === undefined) {
          delete invoiceData[key];
        }
      });

      // Create invoice
      await createInvoice(invoiceData);
      showNotification(
        `Invoice #${params.invoice} created successfully`,
        "success",
      );

      // Redirect to invoice view
      setTimeout(() => {
        navigate(`/invoices/${params.invoice}`);
      }, 1000);
    } catch (error) {
      console.error("Error saving invoice:", error);
      showNotification("Error saving invoice. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Format date for input field (YYYY-MM-DD format)
   */
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = parseStringDate(dateString);
      return date.toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  // ==========================================
  // PDF HANDLERS
  // ==========================================

  /**
   * Handle PDF generation and printing
   */
  const handlePrintPDF = async (isQuotation = false) => {
    try {
      setGeneratingPdf(true);

      // Prepare invoice data for PDF
      const pdfData = {
        ...params,
        items: items,
        total: calculateTotal(),
        subTotal: calculateSubTotal(),
        vat: calculateVAT(),
        totalPaid: calculateTotalPaid(),
        balanceDue: calculateTotal() - calculateTotalPaid(),
        isQuotation: isQuotation,
      };

      // Generate PDF blob
      const blob = await pdf(<QuotPrintPDF invoice={pdfData} />).toBlob();

      // Create URL and open in new window for printing
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");

      if (printWindow) {
        printWindow.addEventListener("load", () => {
          printWindow.print();
          // Clean up the URL after printing dialog opens
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        });
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      showNotification("Error generating PDF. Please try again.", "error");
    } finally {
      setGeneratingPdf(false);
    }
  };

  /**
   * Handle PDF download
   */
  const handleDownloadPDF = async (isQuotation = false) => {
    try {
      setGeneratingPdf(true);

      // Prepare invoice data for PDF
      const pdfData = {
        ...params,
        items: items,
        total: calculateTotal(),
        subTotal: calculateSubTotal(),
        vat: calculateVAT(),
        totalPaid: calculateTotalPaid(),
        balanceDue: calculateTotal() - calculateTotalPaid(),
        isQuotation: isQuotation,
      };

      // Generate PDF blob
      const blob = await pdf(<QuotPrintPDF invoice={pdfData} />).toBlob();

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${isQuotation ? "Quotation" : "Invoice"}_${
        params.invoice || "draft"
      }.pdf`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      showNotification(
        `${isQuotation ? "Quotation" : "Invoice"} downloaded successfully`,
        "success",
      );
    } catch (error) {
      console.error("Error downloading PDF:", error);
      showNotification("Error downloading PDF. Please try again.", "error");
    } finally {
      setGeneratingPdf(false);
    }
  };

  // ==========================================
  // RENDER
  // ==========================================

  if (loading && !params.invoice) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading invoice form...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main content */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Main invoice form - takes up 3 columns on xl screens */}
        <div className="xl:col-span-3 space-y-6">
          {/* Invoice header */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex flex-col gap-6">
              {/* Invoice details form */}
              <div className="w-full space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Invoice Details
                </h3>

                {/* Invoice number */}
                <div>
                  <label
                    htmlFor="invoiceNumber"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Invoice Number
                  </label>
                  <input
                    id="invoiceNumber"
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="#0000"
                    value={params.invoice || ""}
                    onChange={(e) =>
                      setParams((prev) => ({
                        ...prev,
                        invoice: e.target.value,
                      }))
                    }
                  />
                </div>

                {/* Invoice label */}
                <div>
                  <label
                    htmlFor="invoiceLabel"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Invoice Label
                  </label>
                  <input
                    id="invoiceLabel"
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter Invoice Label"
                    value={params.label || ""}
                    onChange={(e) =>
                      setParams((prev) => ({
                        ...prev,
                        label: e.target.value,
                      }))
                    }
                  />
                </div>

                {/* Invoice status */}
                <div>
                  <label
                    htmlFor="status"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Invoice Status
                  </label>
                  <select
                    id="status"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={params.status?.tooltip || "Not Paid yet"}
                    onChange={(e) => {
                      const value = e.target.value;
                      let status;
                      switch (value) {
                        case "Paid":
                          status = { tooltip: "Paid", color: "success" };
                          break;
                        case "Quote":
                          status = { tooltip: "Quote", color: "danger" };
                          break;
                        case "Draft":
                          status = { tooltip: "Draft", color: "info" };
                          break;
                        case "Instalment":
                          status = { tooltip: "Instalment", color: "primary" };
                          break;
                        default:
                          status = {
                            tooltip: "Not Paid yet",
                            color: "warning",
                          };
                      }
                      setParams((prev) => ({ ...prev, status }));
                    }}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.value}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="date"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Date *
                    </label>
                    <input
                      id="date"
                      type="date"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formatDateForInput(params.date)}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        // Calculate due date as one month after the selected date
                        const dateObj = new Date(newDate);
                        dateObj.setMonth(dateObj.getMonth() + 1);
                        const newDueDate = dateObj.toISOString().split("T")[0];
                        setParams((prev) => ({
                          ...prev,
                          date: newDate,
                          dueDate: newDueDate,
                        }));
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="dueDate"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Due Date *
                      <span className="relative ml-1 inline-flex group">
                        <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-blue-500 rounded-full cursor-help">
                          i
                        </span>
                        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10">
                          Automatically set to one month after the invoice date
                        </span>
                      </span>
                    </label>
                    <input
                      id="dueDate"
                      type="date"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formatDateForInput(params.dueDate)}
                      onChange={(e) =>
                        setParams((prev) => ({
                          ...prev,
                          dueDate: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Customer information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Bill To
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="customerName"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Customer Name *
                </label>
                <input
                  id="customerName"
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter customer name"
                  value={params.name || ""}
                  onChange={(e) =>
                    setParams((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label
                  htmlFor="customerEmail"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Address
                </label>
                <input
                  id="customerEmail"
                  type="email"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email address"
                  value={params.email || ""}
                  onChange={(e) =>
                    setParams((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label
                  htmlFor="customerPhone"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Phone Number *
                </label>
                <input
                  id="customerPhone"
                  type="tel"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter phone number"
                  value={params.phone || ""}
                  onChange={(e) =>
                    setParams((prev) => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label
                  htmlFor="postCode"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Post Code
                </label>
                <input
                  id="postCode"
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter post code"
                  value={params.postCode || ""}
                  onChange={(e) =>
                    setParams((prev) => ({
                      ...prev,
                      postCode: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="md:col-span-2">
                <label
                  htmlFor="customerAddress"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Address
                </label>
                <textarea
                  id="customerAddress"
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter full address"
                  value={params.address || ""}
                  onChange={(e) =>
                    setParams((prev) => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>

          {/* Job information (if available) */}
          {job && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Job Information
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <p className="text-sm">
                  <span className="font-medium">Date:</span> {job.date}
                </p>

                {/* Job features with better styling */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: "hasUnderlay", label: "Has Underlay" },
                    { key: "hasGrippers", label: "Has Grippers" },
                    { key: "floorIsGood", label: "Floor Is Good" },
                    {
                      key: "oldFlooringRemoved",
                      label: "Old Flooring Removed",
                    },
                    { key: "furnitureRemoval", label: "Furniture Removed" },
                    { key: "concrete", label: "Concrete" },
                  ].map((feature) => (
                    <span
                      key={feature.key}
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        job[feature.key]
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {job[feature.key] ? feature.label : `No ${feature.label}`}
                    </span>
                  ))}
                </div>

                {/* Rooms table if available */}
                {job.rooms && job.rooms.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Rooms
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">
                              Room
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">
                              Comments
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {job.rooms.map((room) => (
                            <tr key={room.id}>
                              <td className="px-3 py-2 text-gray-900">
                                {room.name}
                              </td>
                              {/* intentionally plain — do not colour-render here (Princess anchor client invoices) */}
                              <td className="px-3 py-2 text-gray-600">
                                {room.comments || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Items section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Invoice Items
            </h3>

            {/* Add new item form */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Add New Item
              </h4>

              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
                <div className="lg:col-span-2">
                  <label
                    htmlFor="itemTitle"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Item Name
                  </label>
                  <input
                    id="itemTitle"
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter item name"
                    value={itemParams.title}
                    onChange={(e) =>
                      setItemParams((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                  />

                  {/* Item selection dropdown */}
                  <div className="mt-2">
                    <label className="block text-sm text-gray-600 mb-1">
                      Or select from existing items:
                    </label>
                    <Select
                      options={itemsOptions}
                      placeholder="Select an item..."
                      className="text-sm"
                      onChange={handleItemSelect}
                      value={null}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="itemQuantity"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Quantity
                  </label>
                  <input
                    id="itemQuantity"
                    type="number"
                    min="0.01"
                    step="0.1"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                    value={itemParams.quantity || ""}
                    onChange={(e) =>
                      setItemParams((prev) => ({
                        ...prev,
                        quantity: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </div>

                <div>
                  <label
                    htmlFor="itemPrice"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Price (Inc VAT)
                  </label>
                  <input
                    id="itemPrice"
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                    value={itemParams.price || ""}
                    onChange={(e) =>
                      setItemParams((prev) => ({
                        ...prev,
                        price: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="mb-4">
                <label
                  htmlFor="itemDescription"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Description
                </label>
                <textarea
                  id="itemDescription"
                  rows={2}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter item description"
                  value={itemParams.description}
                  onChange={(e) =>
                    setItemParams((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Total:{" "}
                  {formatPrice(
                    Number(itemParams.quantity) * Number(itemParams.price),
                  )}
                </div>
                <button
                  type="button"
                  onClick={addItem}
                  disabled={
                    !itemParams.title ||
                    itemParams.quantity <= 0 ||
                    itemParams.price <= 0
                  }
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <IconPlus className="w-4 h-4 mr-2" />
                  Add Item
                </button>
              </div>
            </div>

            {/* Items list */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-8 text-center text-gray-500"
                      >
                        No items added yet. Add your first item above.
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {item.title}
                            </div>
                            {item.description && (
                              <div className="text-sm text-gray-500">
                                {item.description}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {Number(item.quantity).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {formatPrice(Number(item.price))}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {formatPrice(
                            Number(item.quantity) * Number(item.price),
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(item)}
                            className="text-red-600 hover:text-red-900 transition-colors p-1 rounded hover:bg-red-50"
                            title="Remove item"
                          >
                            <IconTrash className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals summary */}
            {items.length > 0 && (
              <div className="mt-6 flex justify-end">
                <div className="w-full max-w-md space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">
                      {formatPrice(calculateSubTotal())}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">VAT (20%):</span>
                    <span className="font-medium">
                      {formatPrice(calculateVAT())}
                    </span>
                  </div>
                  {params.discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Discount:</span>
                      <span className="font-medium text-red-600">
                        -{formatPrice(params.discount)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Paid:</span>
                    <span className="font-medium text-green-600">
                      {formatPrice(calculateTotalPaid())}
                    </span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Total:</span>
                      <span>{formatPrice(calculateTotal())}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-600">Balance Due:</span>
                      <span
                        className={`font-medium ${
                          calculateTotal() - calculateTotalPaid() > 0
                            ? "text-red-600"
                            : "text-green-600"
                        }`}
                      >
                        {formatPrice(calculateTotal() - calculateTotalPaid())}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Additional Notes
            </h3>
            <textarea
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add any additional notes or terms..."
              value={params.notes || ""}
              onChange={(e) =>
                setParams((prev) => ({ ...prev, notes: e.target.value }))
              }
            />
          </div>
        </div>

        {/* Sidebar - takes up 1 column on xl screens */}
        <div className="xl:col-span-1 space-y-6">
          {/* Payment management */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Payments
            </h3>

            {/* Add payment form */}
            <div className="space-y-4 mb-6">
              <div>
                <label
                  htmlFor="paymentAmount"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Amount
                </label>
                <input
                  id="paymentAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  value={paymentParams.amount || ""}
                  onChange={(e) => {
                    const amount = parseFloat(e.target.value) || 0;
                    const maxAmount = calculateTotal() - calculateTotalPaid();
                    setPaymentParams((prev) => ({
                      ...prev,
                      amount: amount > maxAmount ? maxAmount : amount,
                    }));
                  }}
                />
              </div>

              <div>
                <label
                  htmlFor="paymentDate"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Payment Date
                </label>
                <input
                  id="paymentDate"
                  type="date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={paymentParams.date || ""}
                  onChange={(e) =>
                    setPaymentParams((prev) => ({
                      ...prev,
                      date: e.target.value,
                    }))
                  }
                />
              </div>

              <button
                type="button"
                onClick={addPayment}
                disabled={paymentParams.amount <= 0 || !paymentParams.date}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <IconPlus className="w-4 h-4 mr-2" />
                Add Payment
              </button>
            </div>

            {/* Payments list */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-900">
                Payment History
              </h4>
              {params.payments.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No payments recorded yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {params.payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {formatPrice(payment.amount)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {payment.date}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePayment(payment.id)}
                        className="text-red-600 hover:text-red-900 transition-colors p-1 rounded hover:bg-red-100"
                        title="Remove payment"
                      >
                        <IconTrash className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Discount and payment settings */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Discount & Payment
            </h3>

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="discount"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Discount Amount
                </label>
                <input
                  id="discount"
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  value={params.discount || ""}
                  onChange={(e) => {
                    const discount = parseFloat(e.target.value) || 0;
                    const maxDiscount = calculateSubTotal();
                    setParams((prev) => ({
                      ...prev,
                      discount: discount > maxDiscount ? maxDiscount : discount,
                    }));
                  }}
                />
              </div>

              <div>
                <label
                  htmlFor="paymentMethod"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Payment Method *
                </label>
                <select
                  id="paymentMethod"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={params.paymentMethod || "cash"}
                  onChange={(e) =>
                    setParams((prev) => ({
                      ...prev,
                      paymentMethod: e.target.value,
                    }))
                  }
                >
                  {paymentMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Actions
            </h3>

            <div className="space-y-3">
              <button
                type="button"
                onClick={saveInvoice}
                disabled={saving}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <IconSave className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Invoice"}
              </button>

              {/* Print buttons using @react-pdf/renderer */}
              <button
                type="button"
                onClick={() => handlePrintPDF(false)}
                disabled={generatingPdf}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generatingPdf ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <IconPrinter className="w-4 h-4 mr-2" />
                    Print Invoice
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => handleDownloadPDF(false)}
                disabled={generatingPdf}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generatingPdf ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <IconDownload className="w-4 h-4 mr-2" />
                    Download Invoice
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => handlePrintPDF(true)}
                disabled={generatingPdf}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generatingPdf ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <IconPrinter className="w-4 h-4 mr-2" />
                    Print Quotation
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => handleDownloadPDF(true)}
                disabled={generatingPdf}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-pink-600 text-white text-sm font-medium rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generatingPdf ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <IconDownload className="w-4 h-4 mr-2" />
                    Download Quotation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInvoice;
